/**
 * Agency Site Locations — contracts.
 *
 * Where an account physically is: every site an agency operates, its address, a
 * map link, how many people work there, and the technology deployed at it.
 * The org chart one layer down. Full design: docs/AGENCY-SITE-LOCATIONS-ROADMAP.md.
 *
 * Line: CONTEXT (account knowledge), same seam as contacts and notes.
 *
 * Two vocabularies matter here and they are NOT the same list:
 *
 *   SiteType         our SALES taxonomy (~12 values). What a rep thinks about.
 *   SpiStructureType DGS's FACILITIES taxonomy (38 observed values, including
 *                    DAIRY, CHAPEL and COMFORT STATION). Kept as free text on
 *                    structures, never promoted into SiteType.
 *
 * Collapsing them would put a restroom in the Locations tab, which is §3.2 of
 * the roadmap and the reason the model has two levels at all.
 */

/* ─────────────────────────── site identity ─────────────────────────── */

/**
 * What kind of place this is, in sales terms.
 *
 * `public_counter` is deliberately distinct from `field_office`: a DMV counter
 * and an EDD branch office have very different footprints and buying patterns,
 * and merging them loses the distinction a territory plan runs on.
 */
export type SiteType =
  | 'hq'
  | 'office'
  | 'field_office'
  | 'public_counter'
  | 'data_center'
  | 'lab'
  | 'warehouse'
  | 'yard_depot'
  | 'training'
  | 'institution'   // prison, state hospital, veterans home
  | 'remote_hub'
  | 'other';

export const SITE_TYPES: readonly SiteType[] = [
  'hq', 'office', 'field_office', 'public_counter', 'data_center', 'lab',
  'warehouse', 'yard_depot', 'training', 'institution', 'remote_hub', 'other',
] as const;

/**
 * The types the Locations tab renders by default (decision §12.2). Everything
 * else is ingested and one "show all facilities" toggle away.
 *
 * This is a RENDER filter, never an ingest filter — changing it must never
 * require a re-pull.
 */
export const STAFFED_SITE_TYPES: readonly SiteType[] = [
  'hq', 'office', 'field_office', 'public_counter', 'data_center', 'lab',
  'training', 'institution', 'remote_hub',
] as const;

export function isStaffedSiteType(t: SiteType): boolean {
  return STAFFED_SITE_TYPES.includes(t);
}

export const SITE_TYPE_LABELS: Record<SiteType, string> = {
  hq: 'Headquarters',
  office: 'Office',
  field_office: 'Field office',
  public_counter: 'Public counter',
  data_center: 'Data center',
  lab: 'Lab',
  warehouse: 'Warehouse',
  yard_depot: 'Yard / depot',
  training: 'Training facility',
  institution: 'Institution',
  remote_hub: 'Remote hub',
  other: 'Other',
};

/**
 * Lifecycle. `candidate` is anything a machine or an assistant proposed;
 * `unattached` is §12.3's UC/CSU case — a real place with no account yet.
 *
 * There is no `deleted`. A site that vanishes upstream becomes `closed` with a
 * note: "they used to be at 2nd and Capitol" is intelligence, and a hard delete
 * on an upstream blip is unrecoverable.
 */
export type SiteStatus = 'candidate' | 'active' | 'closed' | 'rejected' | 'unattached';

export const SITE_STATUSES: readonly SiteStatus[] = [
  'candidate', 'active', 'closed', 'rejected', 'unattached',
] as const;

/** Which tier of the fill ladder (roadmap §6) put this row here. */
export type SiteSource =
  | 'spi'        // tier 0 — DGS Statewide Property Inventory bulk pull
  | 'catalog'    // tier 1 — curated occupancy catalog
  | 'locator'    // tier 2 — the agency's own published office locator
  | 'search'     // tier 3 — web search
  | 'assistant'  // tier 4 — MCP submission (Grok / Claude / any client)
  | 'manual'     // tier 5 — a human typed it
  | 'notes';     // mined from account notes

export const SITE_SOURCES: readonly SiteSource[] = [
  'spi', 'catalog', 'locator', 'search', 'assistant', 'manual', 'notes',
] as const;

/* ─────────────────────────── headcount ─────────────────────────── */

/**
 * Banded headcount. A band is always available; an exact count often is not,
 * and a web source that says "about 200 staff" should not be forced into a
 * false integer.
 */
export type EmployeeBand = '1-10' | '11-50' | '51-200' | '201-1000' | '1000+';

export const EMPLOYEE_BANDS: readonly EmployeeBand[] = [
  '1-10', '11-50', '51-200', '201-1000', '1000+',
] as const;

export function bandForCount(n: number): EmployeeBand | null {
  if (!Number.isFinite(n) || n < 0) return null;
  if (n <= 10) return '1-10';
  if (n <= 50) return '11-50';
  if (n <= 200) return '51-200';
  if (n <= 1000) return '201-1000';
  return '1000+';
}

/**
 * Square feet per person used to derive a band when nobody has told us the real
 * number (decision §12.4). The range is deliberately wide — it produces an
 * honest span ("~120-190"), not a fake point estimate.
 */
export const SQ_FT_PER_PERSON = { low: 150, high: 250 } as const;

/**
 * The ONLY site types whose square footage says anything about headcount.
 *
 * A 40,000 sq ft data center might hold four people; a prison's square footage
 * is dominated by housing units, not staff. Deriving a band for those would be
 * confidently wrong, which §12.4 rates as worse than blank.
 */
export const DERIVABLE_HEADCOUNT_TYPES: readonly SiteType[] = [
  'hq', 'office', 'field_office', 'public_counter',
] as const;

export interface DerivedHeadcount {
  band: EmployeeBand;
  low: number;
  high: number;
  sqFt: number;
  /** Always true for a derived value — it is an estimate, never an observation. */
  guessed: true;
}

/**
 * Derive a headcount band from square footage, or null when the site type makes
 * that meaningless. Pure — no rounding surprises, no I/O.
 */
export function deriveHeadcount(sqFt: number, siteType: SiteType): DerivedHeadcount | null {
  if (!DERIVABLE_HEADCOUNT_TYPES.includes(siteType)) return null;
  if (!Number.isFinite(sqFt) || sqFt <= 0) return null;
  const high = Math.round(sqFt / SQ_FT_PER_PERSON.low);
  const low = Math.round(sqFt / SQ_FT_PER_PERSON.high);
  if (high < 1) return null;
  // Band off the midpoint: the span is what we show, the band is what we filter.
  const band = bandForCount(Math.round((low + high) / 2));
  if (!band) return null;
  return { band, low, high, sqFt, guessed: true };
}

/* ─────────────────────────── technology at a site ─────────────────────────── */

/**
 * What a box DOES at a site. Deliberately role-shaped rather than
 * product-shaped: "3 access switches and an edge firewall" is the sentence a
 * rep says, and it survives not knowing the model number.
 */
export type SiteTechRole =
  | 'edge_firewall'
  | 'core_switch'
  | 'access_switch'
  | 'router'
  | 'wifi'
  | 'server'
  | 'storage'
  | 'wan_circuit'
  | 'cloud_onramp'
  | 'voice'
  | 'endpoint'
  | 'other';

export const SITE_TECH_ROLES: readonly SiteTechRole[] = [
  'edge_firewall', 'core_switch', 'access_switch', 'router', 'wifi', 'server',
  'storage', 'wan_circuit', 'cloud_onramp', 'voice', 'endpoint', 'other',
] as const;

export const SITE_TECH_ROLE_LABELS: Record<SiteTechRole, string> = {
  edge_firewall: 'Edge firewall',
  core_switch: 'Core switch',
  access_switch: 'Access switch',
  router: 'Router',
  wifi: 'Wireless',
  server: 'Server',
  storage: 'Storage',
  wan_circuit: 'WAN circuit',
  cloud_onramp: 'Cloud on-ramp',
  voice: 'Voice',
  endpoint: 'Endpoints',
  other: 'Other',
};

/**
 * `rejected` and `retired` are NOT the same answer and must not be merged.
 *
 *   retired  — it was there and is gone. A true fact about the past.
 *   rejected — it was never there. A human turned the claim down.
 *
 * The distinction is load-bearing on the assistant surface: a resubmission of
 * a retired row is worth re-reading ("did they put it back?"), and a
 * resubmission of a rejected one is the assistant repeating a mistake it has
 * already been corrected on. Collapsing the two would make the dedupe reply
 * unable to tell it which.
 */
export type SiteTechStatus = 'candidate' | 'active' | 'retired' | 'rejected';

/**
 * Runtime twin of SiteTechStatus. A type alone cannot be validated against at
 * run time, and the write path has to refuse a bad status with a sentence
 * rather than let Postgres raise a 23514.
 */
export const SITE_TECH_STATUSES: readonly SiteTechStatus[] = [
  'candidate', 'active', 'retired', 'rejected',
] as const;

/** Tech statuses a site's own panel shows. The estate, not its history. */
export const VISIBLE_SITE_TECH_STATUSES: readonly SiteTechStatus[] = ['active'] as const;

/**
 * Open attribute bag for facts that are not columns.
 *
 * "Connected to Amazon at 10G via Equinix SV5" is a fact about ONE data center.
 * Typing that into columns before we have twenty examples would be guessing at
 * a schema; these keys are conventions, and unknown keys are preserved.
 */
export interface SiteTechAttributes {
  throughputGbps?: number;
  carriers?: string[];
  cloudProvider?: string;
  cloudOnrampType?: string;   // 'DirectConnect' | 'ExpressRoute' | 'Interconnect' | …
  rackCount?: number;
  powerKw?: number;
  model?: string;
  version?: string;
  [key: string]: unknown;
}

/* ─────────────────────────── provenance ─────────────────────────── */

/**
 * Identical in shape and rules to contacts.field_provenance
 * (apps/backend/services/crm/orgChartCore.js). Same three statuses, same
 * ranking, same one rule:
 *
 *   an automated write fills blanks and may replace another automated value;
 *   it NEVER replaces a human value.
 *
 * That rule is what makes the weekly SPI re-ingest safe. Without it the second
 * pull erases every hand-fix and the feature dies the first time it happens.
 */
export type ProvenanceStatus = 'automated' | 'verified' | 'field';

export interface SiteFieldProvenance {
  status: ProvenanceStatus;
  /** Inferred rather than observed — a derived band, a type read off a page title. */
  guessed: boolean;
  confidence: number;      // 0-100
  basis: string | null;    // how it was determined, human-readable
  evidenceUrl: string | null;
  actor: string | null;    // who wrote it (email = human, anything else = automated)
  via: string | null;      // which client, when an assistant wrote it
  at: string;              // ISO 8601
  confirmedBy?: string;    // only on status 'field'
}

/** Rank used by the one rule. A field with a value but NO entry predates
 *  provenance and ranks as a human entry — same as the org chart. */
export const PROVENANCE_RANK: Record<ProvenanceStatus, number> = {
  automated: 0,
  verified: 1,
  field: 2,
};

/** Fields on a site whose provenance we track individually. */
export const TRACKED_SITE_FIELDS = [
  'site_name', 'site_type', 'address_line1', 'address_line2', 'city',
  'state', 'postal_code', 'county', 'latitude', 'longitude',
  'employee_count', 'employee_band', 'is_primary',
] as const;

export type TrackedSiteField = typeof TRACKED_SITE_FIELDS[number];

/** True when the actor string is a machine (a key, a pipeline, a client slug)
 *  rather than a person's email address. Mirrors isAutomatedActor(). */
export function isAutomatedActor(actor: string | null | undefined): boolean {
  return !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(actor || ''));
}

/**
 * May an automated write land on a field that currently holds `existing`?
 *
 * The whole one rule, in one function, so no caller can reimplement it
 * slightly differently.
 */
export function automatedWriteAllowed(existing: SiteFieldProvenance | null | undefined): boolean {
  if (existing === null || existing === undefined) return false; // no entry = human, hands off
  return existing.status === 'automated';
}

/* ─────────────────────────── geo ─────────────────────────── */

/**
 * California's bounding box, used to reject the geo junk SPI ships.
 *
 * One live example (2026-09-10): a CAL FIRE storage shed in Yreka carries
 * longitude **+122.641156** — a sign flip that places it in Mongolia. Slightly
 * padded so a coastal or border site is never rejected for being near the edge.
 */
export const CA_BOUNDS = {
  minLat: 32.0,
  maxLat: 42.5,
  minLon: -125.0,
  maxLon: -113.5,
} as const;

export interface GeoCheck {
  ok: boolean;
  latitude: number | null;
  longitude: number | null;
  /** Set when a positive longitude was flipped back into the box. */
  signCorrected: boolean;
  reason: string | null;
}

/**
 * Is this coordinate field ABSENT, as opposed to zero?
 *
 * `Number(null)`, `Number('')` and `Number(false)` are all 0, which is finite
 * — so any has-a-coordinate test written as `Number.isFinite(Number(v))`
 * reports that a site with NO coordinate HAS one at null island. checkGeo got
 * that right from the start; validateSiteInput did not, and every site without
 * a point was uneditable until #243.
 *
 * ONE definition per file, because both of them need it and they must never
 * answer differently. It was written out twice in each copy of this contract —
 * four in total — and the hazard the #243 review named was someone tightening
 * or loosening one and leaving the same file disagreeing with itself about
 * what "present" means. The core/mirror duplication that remains is deliberate
 * and guarded: `npm run sites:vocab-check`, run by CI since #258.
 */
const absent = (v: unknown): boolean => v === null || v === undefined
  || (typeof v === 'string' && v.trim() === '')
  || typeof v === 'boolean';

/**
 * Validate a coordinate pair, correcting a sign-flipped longitude ONLY when
 * the flip lands inside California. Anything else is rejected rather than
 * guessed at — the address is still good, we just have no usable point.
 */
export function checkGeo(lat: unknown, lon: unknown): GeoCheck {
  const miss = (reason: string): GeoCheck =>
    ({ ok: false, latitude: null, longitude: null, signCorrected: false, reason });

  // Absent, not zero — see `absent` above for why that distinction exists.
  if (absent(lat) || absent(lon)) return miss('missing');

  const la = Number(lat);
  const lo = Number(lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return miss('missing');
  if (la === 0 && lo === 0) return miss('null island');

  const inBox = (a: number, o: number) =>
    a >= CA_BOUNDS.minLat && a <= CA_BOUNDS.maxLat &&
    o >= CA_BOUNDS.minLon && o <= CA_BOUNDS.maxLon;

  if (inBox(la, lo)) {
    return { ok: true, latitude: la, longitude: lo, signCorrected: false, reason: null };
  }
  if (lo > 0 && inBox(la, -lo)) {
    return { ok: true, latitude: la, longitude: -lo, signCorrected: true, reason: 'longitude sign flipped' };
  }
  return miss('outside California');
}

/**
 * A Google Maps link that needs no API key, no billing account and no
 * coordinates — so it works on a hand-typed row too (roadmap §6).
 *
 * Prefers the address, because a human recognises "1500 Capitol Ave" and cannot
 * check a lat/lng. Falls back to the point when there is no address at all.
 */
export function mapsUrl(site: Pick<AgencySite,
  'addressLine1' | 'city' | 'state' | 'postalCode' | 'latitude' | 'longitude'>
  & { googlePlaceId?: string | null }): string | null {
  // A street or a city is REQUIRED. State and postal code alone would build a
  // link to a ZIP-code centroid — a pin that looks authoritative and is not
  // the building. Better to say we cannot map it.
  const street = site.addressLine1 == null ? '' : String(site.addressLine1).trim();
  const city = site.city == null ? '' : String(site.city).trim();
  const parts = (street || city)
    ? [site.addressLine1, site.city, site.state, site.postalCode]
      .map((p) => (p == null ? '' : String(p).trim()))
      .filter(Boolean)
    : [];
  let query: string | null = null;
  if (parts.length > 0) {
    query = encodeURIComponent(parts.join(', '));
  } else if (typeof site.latitude === 'number' && typeof site.longitude === 'number') {
    query = `${site.latitude},${site.longitude}`;
  }
  if (query === null) return null;

  // A held place ID refines the pin: Maps opens on THAT listing rather than on
  // a text search for the street, which is the difference between the building
  // and the block. It never replaces the query — the Maps URL scheme requires
  // one, and a place ID with nothing to anchor it is not a link we can stand
  // behind (the resolver only ever matched it AGAINST an address or a point).
  const placeId = typeof site.googlePlaceId === 'string' ? site.googlePlaceId.trim() : '';
  const pin = placeId ? `&query_place_id=${encodeURIComponent(placeId)}` : '';
  return `https://www.google.com/maps/search/?api=1&query=${query}${pin}`;
}

/* ─────────────────────────── payloads ─────────────────────────── */

export interface AgencySiteStructure {
  structureId: string;
  siteId: string;
  spiStructureNumber: number | null;
  structureName: string | null;
  /** DGS's facilities taxonomy, verbatim. Never mapped onto SiteType. */
  structureType: string | null;
  sqFt: number | null;
  yearBuilt: number | null;
  source: SiteSource;
}

export interface AgencySiteTech {
  siteTechId: string;
  siteId: string;
  techComponentId: number | null;
  /** What it was called when we could not resolve a catalog component. */
  rawLabel: string | null;
  role: SiteTechRole;
  quantity: number | null;
  attributes: SiteTechAttributes;
  status: SiteTechStatus;
  confidence: number | null;
  provenance: Record<string, SiteFieldProvenance>;
  updatedAt: string;
}

export interface AgencySite {
  siteId: string;
  /** The OCCUPANT. Null only for §12.3 unattached rows, which no
   *  account-scoped read returns. */
  departmentId: number | null;
  siteName: string;
  siteType: SiteType;
  status: SiteStatus;

  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  county: string | null;
  latitude: number | null;
  longitude: number | null;

  /**
   * The ONE Google Places field that may be stored (Google exempts the place
   * ID from its caching restrictions and forbids retaining the rest). Set only
   * when a Places candidate sat within the match radius of DGS's coordinate —
   * a corroborated pin, not a guess. Null = never matched, or not yet checked;
   * the UI must not tell those apart from this field alone.
   */
  googlePlaceId: string | null;

  employeeCount: number | null;
  employeeBand: EmployeeBand | null;
  /** Present when the band was derived from square footage rather than observed. */
  employeeEstimate: DerivedHeadcount | null;

  isPrimary: boolean;

  spiRealPropertyNumber: number | null;
  spiAgencyUcm: number | null;
  /** Retained so an unattached row stays attributable until an account exists. */
  spiAgencyName: string | null;

  source: SiteSource;
  confidence: number | null;
  provenance: Record<TrackedSiteField, SiteFieldProvenance> | Record<string, SiteFieldProvenance>;

  lastConfirmedAt: string | null;
  createdAt: string;
  updatedAt: string;

  /** Convenience, computed server-side so every surface links identically. */
  mapsUrl: string | null;

  structures?: AgencySiteStructure[];
  tech?: AgencySiteTech[];
}

/**
 * Per-account coverage. Rendered on the tab so "42 sites" is never mistaken for
 * "all 42 sites" — the roadmap §3.4 honesty rule, in the payload rather than in
 * a comment nobody reads.
 */
export interface SiteCoverage {
  total: number;
  staffed: number;
  bySource: Partial<Record<SiteSource, number>>;
  withAddress: number;
  withGeo: number;
  humanVerified: number;
  lastIngestAt: string | null;
  /** Set when the seed is known to under-report this agency (tenant agencies,
   *  §3.1). Drives the "SPI covers owners, not tenants" note on the tab. */
  seedUnderreports: boolean;
}

export interface AgencySitesResponse {
  departmentId: number;
  acronym: string | null;
  sites: AgencySite[];
  coverage: SiteCoverage;
}

/* ─────────────────────────── worklist (MCP) ─────────────────────────── */

/**
 * What the remote MCP is allowed to see (decision §12.1).
 *
 * There is deliberately NO address, NO headcount and NO tech on this shape. A
 * worklist tells an assistant where to go looking; it does not hand over the
 * facility picture. If a field is added here, re-read §8.1 first.
 */
export interface SiteWorklistEntry {
  acronym: string;
  departmentName: string;
  siteCount: number;
  staffedSiteCount: number;
  /** Null when we have no basis for an expectation — never a fabricated target. */
  expectedSiteCount: number | null;
  /** The agency's own site, so the assistant starts somewhere sensible. */
  website: string | null;
  lastResearchedAt: string | null;
  candidateCount: number;
}

export interface SiteWorklistResponse {
  entries: SiteWorklistEntry[];
  generatedAt: string;
}

/* ─────────────────────────── validation ─────────────────────────── */

export interface SiteValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate a submitted site. Shared by the route, the MCP submit verb and the
 * UI so all three refuse the same things for the same reasons.
 *
 * Note what is NOT checked: geography implied by a name. "FTB LOS ANGELES -
 * BLDG 1" sits at 9645 Butterfield Way in *Sacramento* — parsing a place out of
 * a name invents sites that do not exist (roadmap hurdle 3). Address fields are
 * the only source of place, and a name is only ever a label.
 */
export function validateSiteInput(input: Partial<AgencySite>): SiteValidationResult {
  const errors: string[] = [];

  const name = String(input.siteName || '').trim();
  if (!name) errors.push('siteName is required');
  else if (name.length > 200) errors.push('siteName must be 200 characters or fewer');

  if (!input.siteType || !SITE_TYPES.includes(input.siteType as SiteType)) {
    errors.push(`siteType must be one of: ${SITE_TYPES.join(', ')}`);
  }
  if (input.status && !SITE_STATUSES.includes(input.status as SiteStatus)) {
    errors.push(`status must be one of: ${SITE_STATUSES.join(', ')}`);
  }
  if (input.source && !SITE_SOURCES.includes(input.source as SiteSource)) {
    errors.push(`source must be one of: ${SITE_SOURCES.join(', ')}`);
  }

  // A site with neither an address nor a point is not a location, it is a note.
  const hasAddress = !!String(input.addressLine1 || '').trim() || !!String(input.city || '').trim();
  /*
   * A COORDINATE IS "PRESENT" ONLY IF IT IS ACTUALLY THERE.
   *
   * `Number(null)` is 0, which is finite — so the obvious spelling of this
   * test reported that a site with NO coordinate HAS one, and then rejected it
   * as "coordinates rejected: missing". Create never hit it (an absent field is
   * `undefined`, and `Number(undefined)` is NaN), but `updateSite` builds its
   * validation input from the CURRENT ROW, where an empty coordinate is an
   * explicit `null` out of Postgres.
   *
   * The effect was that **every site without a coordinate was uneditable** —
   * 60 staffed sites, every land row, and every hand-added site. Found on
   * 2026-09-16 correcting one city on a real row, which is the only way a bug
   * like this surfaces: it needs a null from the database, so no test built
   * from a literal ever produces one.
   *
   * `absent` mirrors checkGeo's own rule, which already got this right.
   */
  const hasGeo = !absent(input.latitude) && !absent(input.longitude)
    && Number.isFinite(Number(input.latitude)) && Number.isFinite(Number(input.longitude));
  if (!hasAddress && !hasGeo) {
    errors.push('a site needs an address (line1 or city) or a coordinate pair');
  }

  if (hasGeo) {
    const geo = checkGeo(input.latitude, input.longitude);
    if (!geo.ok) errors.push(`coordinates rejected: ${geo.reason}`);
  }

  if (input.employeeCount != null) {
    const n = Number(input.employeeCount);
    if (!Number.isInteger(n) || n < 0) errors.push('employeeCount must be a non-negative integer');
  }
  if (input.employeeBand && !EMPLOYEE_BANDS.includes(input.employeeBand as EmployeeBand)) {
    errors.push(`employeeBand must be one of: ${EMPLOYEE_BANDS.join(', ')}`);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Normalize the pieces that decide whether two rows are the same place.
 * Used to build `dedupe_key` so SPI, a locator scrape and a hand-typed row all
 * MERGE onto one site instead of racing each other into three.
 */
export function siteDedupeKey(input: {
  departmentId: number | null;
  spiAgencyUcm?: number | null;
  addressLine1: string | null;
  city: string | null;
  siteName: string;
}): string {
  const norm = (s: string | null | undefined) =>
    String(s || '')
      .toLowerCase()
      .replace(/[.,#]/g, ' ')
      // Street-type abbreviations: "1500 Capitol Avenue" and "1500 Capitol Ave"
      // are the same door, and SPI and a locator page will disagree about which.
      .replace(/\b(street|st)\b/g, 'st')
      .replace(/\b(avenue|ave)\b/g, 'ave')
      .replace(/\b(boulevard|blvd)\b/g, 'blvd')
      .replace(/\b(road|rd)\b/g, 'rd')
      .replace(/\b(drive|dr)\b/g, 'dr')
      .replace(/\b(suite|ste)\b/g, 'ste')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  // Scope by the occupant when we have one, else by the SPI owner, so two
  // unattached rows from different agencies at one shared address stay distinct.
  const scope = input.departmentId != null
    ? `d${input.departmentId}`
    : `u${input.spiAgencyUcm ?? 'none'}`;

  const where = norm(input.addressLine1) || norm(input.siteName);
  return `${scope}|${where}|${norm(input.city)}`;
}

/* ═══════════════════ technology at a site — the write contract ═══════════════════ */

/**
 * Tech fields whose provenance we track individually.
 *
 * `attributes` is tracked as ONE unit rather than per-key. A per-key
 * provenance map inside a JSONB bag would be a second provenance system with
 * its own merge rules, and the bag exists precisely because we do not yet know
 * what its keys are (roadmap §4). Whoever last set the bag owns the bag.
 */
export const TRACKED_SITE_TECH_FIELDS = [
  'tech_component_id', 'raw_label', 'role', 'quantity', 'attributes',
] as const;

export type TrackedSiteTechField = typeof TRACKED_SITE_TECH_FIELDS[number];

/**
 * Conventions for the open attribute bag — NOT a whitelist.
 *
 * Unknown keys are preserved (that is the point of the bag), but a known key
 * is coerced to its declared kind so "10" and 10 do not become two different
 * facts about the same on-ramp. `roles` is advisory: it tells the UI which
 * fields to OFFER, never which the row may carry, because the day a branch
 * office turns out to have a rack count is the day a whitelist loses data.
 */
export interface SiteTechAttributeSpec {
  key: string;
  label: string;
  kind: 'number' | 'string' | 'stringList';
  unit?: string;
  /** Roles the UI offers this field for. Advisory only. */
  roles: readonly SiteTechRole[];
  hint?: string;
}

export const SITE_TECH_ATTRIBUTE_SPECS: readonly SiteTechAttributeSpec[] = [
  {
    key: 'model', label: 'Model', kind: 'string',
    roles: SITE_TECH_ROLES, hint: 'PA-3220, Catalyst 9300, MX250',
  },
  {
    key: 'version', label: 'Version', kind: 'string',
    roles: ['edge_firewall', 'core_switch', 'access_switch', 'router', 'wifi', 'server', 'voice', 'endpoint'],
    hint: 'PAN-OS 11.1',
  },
  {
    key: 'throughputGbps', label: 'Throughput', kind: 'number', unit: 'Gbps',
    roles: ['edge_firewall', 'core_switch', 'router', 'wan_circuit', 'cloud_onramp'],
  },
  {
    key: 'carriers', label: 'Carriers', kind: 'stringList',
    roles: ['wan_circuit', 'cloud_onramp'], hint: 'AT&T, Lumen',
  },
  {
    key: 'cloudProvider', label: 'Cloud provider', kind: 'string',
    roles: ['cloud_onramp', 'server', 'storage'], hint: 'AWS, Azure, GCP',
  },
  {
    key: 'cloudOnrampType', label: 'On-ramp type', kind: 'string',
    roles: ['cloud_onramp'], hint: 'Direct Connect, ExpressRoute, Interconnect',
  },
  {
    key: 'exchange', label: 'Exchange / colo', kind: 'string',
    roles: ['cloud_onramp', 'wan_circuit'], hint: 'Equinix SV5',
  },
  { key: 'rackCount', label: 'Racks', kind: 'number', roles: ['server', 'storage', 'other'] },
  { key: 'powerKw', label: 'Power', kind: 'number', unit: 'kW', roles: ['server', 'storage', 'other'] },
  { key: 'capacityTb', label: 'Capacity', kind: 'number', unit: 'TB', roles: ['storage'] },
] as const;

/** Caps on the bag. Sized to hold a generous data-center row and nothing more. */
export const SITE_TECH_ATTRIBUTE_LIMITS = {
  maxKeys: 24,
  maxKeyLength: 60,
  maxStringLength: 300,
  maxListItems: 12,
  /** Serialized ceiling, so a pile of near-limit strings still cannot balloon. */
  maxJsonBytes: 4000,
} as const;

/**
 * Keys that must never reach a JSONB bag built by object assignment.
 *
 * The value goes to Postgres as JSON, so `__proto__` is inert THERE — but the
 * same object is built, merged and read in Node on both sides of the wire, and
 * a bag carrying `__proto__` is one `Object.assign` away from polluting a
 * prototype. Dropped rather than escaped: nothing legitimate is named this.
 */
const FORBIDDEN_ATTRIBUTE_KEYS = ['__proto__', 'constructor', 'prototype'];

export interface AttributeNormalizeResult {
  attributes: SiteTechAttributes;
  errors: string[];
  /** Keys removed, with why — surfaced so a submitter learns rather than guesses. */
  dropped: { key: string; reason: string }[];
}

/**
 * Coerce an incoming attribute bag into something storable.
 *
 * Rules, in order of how much they matter:
 *  - a forbidden or absurd key is DROPPED, never stored;
 *  - a nested object or a function is DROPPED — the bag is flat by design, and
 *    a nested blob is a schema nobody agreed to;
 *  - a known key is coerced to its declared kind, so "10" and 10 agree;
 *  - an unknown scalar or string list is KEPT as-is. That is the whole point
 *    of an open bag: "connected to Amazon at 10G via Equinix SV5" is a fact
 *    about one data center, and we do not have twenty examples yet.
 */
export function normalizeSiteTechAttributes(raw: unknown): AttributeNormalizeResult {
  const errors: string[] = [];
  const dropped: { key: string; reason: string }[] = [];
  const out: SiteTechAttributes = {};

  if (raw === null || raw === undefined) return { attributes: out, errors, dropped };
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return { attributes: out, errors: ['attributes must be an object'], dropped };
  }

  const L = SITE_TECH_ATTRIBUTE_LIMITS;
  const specByKey = new Map(SITE_TECH_ATTRIBUTE_SPECS.map((s) => [s.key, s]));

  // Own enumerable keys only — an inherited key is not a fact anybody typed.
  for (const key of Object.keys(raw as Record<string, unknown>)) {
    if (Object.keys(out).length >= L.maxKeys) {
      dropped.push({ key, reason: `over the ${L.maxKeys}-key limit` });
      continue;
    }
    if (FORBIDDEN_ATTRIBUTE_KEYS.includes(key)) {
      dropped.push({ key, reason: 'reserved key' });
      continue;
    }
    const trimmedKey = key.trim();
    if (!trimmedKey || trimmedKey.length > L.maxKeyLength) {
      dropped.push({ key, reason: 'key is empty or too long' });
      continue;
    }

    const v = (raw as Record<string, unknown>)[key];
    if (v === null || v === undefined || v === '') continue;

    const spec = specByKey.get(trimmedKey);

    if (spec?.kind === 'number') {
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0) {
        errors.push(`${trimmedKey} must be a non-negative number`);
        continue;
      }
      out[trimmedKey] = n;
      continue;
    }

    if (spec?.kind === 'stringList' || Array.isArray(v)) {
      const items = (Array.isArray(v) ? v : String(v).split(','))
        .map((x) => String(x).trim())
        .filter(Boolean)
        .map((x) => x.slice(0, L.maxStringLength));
      const seen = new Set<string>();
      const uniq = items.filter((x) => {
        const k = x.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      if (!uniq.length) continue;
      if (uniq.length > L.maxListItems) {
        dropped.push({ key: trimmedKey, reason: `more than ${L.maxListItems} items` });
        continue;
      }
      out[trimmedKey] = uniq;
      continue;
    }

    if (typeof v === 'object') {
      dropped.push({ key: trimmedKey, reason: 'nested objects are not stored' });
      continue;
    }
    if (typeof v === 'function' || typeof v === 'symbol' || typeof v === 'bigint') {
      dropped.push({ key: trimmedKey, reason: `unsupported value type (${typeof v})` });
      continue;
    }
    if (typeof v === 'boolean') { out[trimmedKey] = v; continue; }
    if (typeof v === 'number') {
      if (!Number.isFinite(v)) { errors.push(`${trimmedKey} must be a finite number`); continue; }
      out[trimmedKey] = v;
      continue;
    }

    const s = String(v).trim();
    if (!s) continue;
    if (s.length > L.maxStringLength) {
      errors.push(`${trimmedKey} must be ${L.maxStringLength} characters or fewer`);
      continue;
    }
    out[trimmedKey] = s;
  }

  if (JSON.stringify(out).length > L.maxJsonBytes) {
    errors.push(`attributes exceed ${L.maxJsonBytes} bytes`);
  }

  return { attributes: out, errors, dropped };
}

export interface SiteTechInput {
  techComponentId?: number | null;
  rawLabel?: string | null;
  role?: SiteTechRole | string;
  quantity?: number | null;
  attributes?: unknown;
  status?: SiteTechStatus | string;
  notes?: string | null;
}

/**
 * Validate a tech row.
 *
 * The one structural rule: a row must NAME something. A quantity with no label
 * and no catalog id is "3 of something, somewhere" — it reads as data, tells
 * nobody anything, and is the exact shape of noise that makes a rep stop
 * trusting the panel. It is also the table's own CHECK constraint
 * (`agency_site_tech_identity_chk`), enforced here so the caller gets a
 * sentence rather than a 23514.
 */
export function validateSiteTechInput(input: SiteTechInput): SiteValidationResult {
  const errors: string[] = [];
  const i = input || {};

  const label = String(i.rawLabel || '').trim();
  const hasComponent = i.techComponentId != null && Number.isInteger(Number(i.techComponentId));
  if (!label && !hasComponent) {
    errors.push('a tech row needs a rawLabel or a techComponentId — name what is there');
  }
  if (label.length > 200) errors.push('rawLabel must be 200 characters or fewer');
  if (i.techComponentId != null && !hasComponent) {
    errors.push('techComponentId must be an integer');
  }

  if (!i.role || !SITE_TECH_ROLES.includes(i.role as SiteTechRole)) {
    errors.push(`role must be one of: ${SITE_TECH_ROLES.join(', ')}`);
  }

  if (i.quantity != null && i.quantity !== ('' as unknown)) {
    const n = Number(i.quantity);
    if (!Number.isInteger(n) || n < 0) errors.push('quantity must be a non-negative integer');
    else if (n > 100000) errors.push('quantity looks wrong (over 100000)');
  }

  if (i.status && !(SITE_TECH_STATUSES as readonly string[]).includes(String(i.status))) {
    errors.push('status must be one of: candidate, active, retired');
  }

  if (i.notes != null && String(i.notes).length > 2000) {
    errors.push('notes must be 2000 characters or fewer');
  }

  if (i.attributes !== undefined) {
    errors.push(...normalizeSiteTechAttributes(i.attributes).errors);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * One tech row as a rep would say it: "3 × Access switch — Catalyst 9300".
 *
 * The quantity leads because that is the question being answered ("how much is
 * there"), and the label trails because it is often missing.
 */
export function siteTechLabel(t: {
  role: SiteTechRole | string;
  quantity?: number | null;
  rawLabel?: string | null;
}): string {
  const role = SITE_TECH_ROLE_LABELS[t.role as SiteTechRole] || 'Other';
  const qty = t.quantity != null && Number.isFinite(Number(t.quantity)) ? `${Number(t.quantity)} × ` : '';
  const label = String(t.rawLabel || '').trim();
  return label ? `${qty}${role} — ${label}` : `${qty}${role}`;
}

/**
 * Roll a site's tech rows up into role counts, in the vocabulary's own order.
 *
 * A row with no quantity counts as ONE thing rather than zero: somebody saw a
 * firewall and did not count it, and rendering that as "0 firewalls" would be
 * worse than the uncertainty it is trying to represent. `exact` says whether
 * every row in that role carried a number, so the UI can write "3" or "3+".
 */
export function summarizeSiteTech(rows: {
  role: SiteTechRole | string;
  quantity?: number | null;
  status?: SiteTechStatus | string;
}[]): { role: SiteTechRole; label: string; count: number; rows: number; exact: boolean }[] {
  const byRole = new Map<SiteTechRole, { count: number; rows: number; exact: boolean }>();

  for (const r of rows || []) {
    if (r.status !== undefined && r.status !== 'active') continue;
    const role = (SITE_TECH_ROLES.includes(r.role as SiteTechRole) ? r.role : 'other') as SiteTechRole;
    const cur = byRole.get(role) || { count: 0, rows: 0, exact: true };
    const q = r.quantity == null ? null : Number(r.quantity);
    cur.count += q != null && Number.isFinite(q) ? q : 1;
    cur.rows += 1;
    if (q == null || !Number.isFinite(q)) cur.exact = false;
    byRole.set(role, cur);
  }

  return SITE_TECH_ROLES
    .filter((role) => byRole.has(role))
    .map((role) => ({
      role,
      label: SITE_TECH_ROLE_LABELS[role],
      ...byRole.get(role)!,
    }));
}

/**
 * The merge point for a tech row.
 *
 * Same reasoning as `siteDedupeKey`: a hand-typed row, an assistant's
 * submission and (one day) a note-mined claim about the same box must MERGE
 * onto one row rather than race each other into three. Identity is
 * (site, role, what it is) — quantity is deliberately absent, because "3
 * access switches" becoming "4 access switches" is the SAME fact updated, not
 * a second fact.
 *
 * A resolved catalog component wins over a label: two spellings of "Catalyst
 * 9300" that both resolve to component 812 are one row.
 */
export function siteTechDedupeKey(input: {
  siteId: string;
  role: SiteTechRole | string;
  techComponentId?: number | null;
  rawLabel?: string | null;
}): string {
  const role = SITE_TECH_ROLES.includes(input.role as SiteTechRole) ? input.role : 'other';
  const what = input.techComponentId != null
    ? `c${input.techComponentId}`
    : String(input.rawLabel || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  return `${input.siteId}|${role}|${what}`;
}
